import { Card, Group, Stack, Tabs, Text, ThemeIcon } from '@mantine/core';
import {
  IconCalendar,
  IconCalendarRepeat,
  IconCreditCard,
  IconGift,
  IconListCheck,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';

import type { RefreshInterval } from '../api/queries';
import { DebtsPanel } from './DebtsPanel';
import { MandatoryPaymentsPanel } from './MandatoryPaymentsPanel';
import { PlannedOperationsPanel } from './PlannedOperationsPanel';
import { WishlistPanel } from './WishlistPanel';

type SidebarTab = 'plans' | 'payments' | 'wishes' | 'debts';
type Counts = Record<SidebarTab, number | null>;

const INITIAL_COUNTS: Counts = {
  plans: null,
  payments: null,
  wishes: null,
  debts: null,
};

export function SidebarPanels({ refreshInterval }: { refreshInterval: RefreshInterval }) {
  const [activeTab, setActiveTab] = useState<SidebarTab>('plans');
  const [counts, setCounts] = useState<Counts>(INITIAL_COUNTS);

  const updateCount = useCallback((tab: SidebarTab, count: number | null) => {
    setCounts((current) =>
      current[tab] === count ? current : { ...current, [tab]: count },
    );
  }, []);
  const countChangeHandlers = useMemo(
    () => ({
      plans: (count: number | null) => updateCount('plans', count),
      payments: (count: number | null) => updateCount('payments', count),
      wishes: (count: number | null) => updateCount('wishes', count),
      debts: (count: number | null) => updateCount('debts', count),
    }),
    [updateCount],
  );

  return (
    <Card padding="md">
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size="md" radius="md" variant="light">
            <IconListCheck size={18} stroke={1.5} />
          </ThemeIcon>
          <div>
            <Text fw={600}>Планы и обязательства</Text>
            <Text size="xs" c="dimmed">
              Планы, регулярные расходы, желания и долги
            </Text>
          </div>
        </Group>

        <Tabs
          value={activeTab}
          onChange={(value) => setActiveTab((value as SidebarTab | null) ?? 'plans')}
          keepMounted
          keepMountedMode="display-none"
        >
          <Tabs.List
            grow
            aria-label="Планы и обязательства"
            style={{ flexWrap: 'nowrap' }}
          >
            <SidebarTabButton
              value="plans"
              label="Планы"
              count={counts.plans}
              icon={<IconCalendar size={15} stroke={1.5} />}
            />
            <SidebarTabButton
              value="payments"
              label="Платежи"
              count={counts.payments}
              icon={<IconCalendarRepeat size={15} stroke={1.5} />}
            />
            <SidebarTabButton
              value="wishes"
              label="Хотелки"
              count={counts.wishes}
              icon={<IconGift size={15} stroke={1.5} />}
            />
            <SidebarTabButton
              value="debts"
              label="Долги"
              count={counts.debts}
              icon={<IconCreditCard size={15} stroke={1.5} />}
            />
          </Tabs.List>

          <Tabs.Panel value="plans" pt="md">
            <PlannedOperationsPanel
              refreshInterval={refreshInterval}
              onCountChange={countChangeHandlers.plans}
            />
          </Tabs.Panel>
          <Tabs.Panel value="payments" pt="md">
            <MandatoryPaymentsPanel
              refreshInterval={refreshInterval}
              onCountChange={countChangeHandlers.payments}
            />
          </Tabs.Panel>
          <Tabs.Panel value="wishes" pt="md">
            <WishlistPanel
              refreshInterval={refreshInterval}
              onCountChange={countChangeHandlers.wishes}
            />
          </Tabs.Panel>
          <Tabs.Panel value="debts" pt="md">
            <DebtsPanel
              refreshInterval={refreshInterval}
              onCountChange={countChangeHandlers.debts}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Card>
  );
}

function SidebarTabButton({
  value,
  label,
  count,
  icon,
}: {
  value: SidebarTab;
  label: string;
  count: number | null;
  icon: React.ReactNode;
}) {
  return (
    <Tabs.Tab value={value} px={2} py="xs" style={{ flex: '1 1 0', minWidth: 0 }}>
      <Stack gap={2} align="center">
        {icon}
        <Text component="span" fz={11} fw={500} style={{ whiteSpace: 'nowrap' }}>
          {label}
          {count !== null && ` · ${count}`}
        </Text>
      </Stack>
    </Tabs.Tab>
  );
}
